import { api } from "./client";

// ── Session ─────────────────────────────────────────────────────────────

export function getCurrentUser() {
  return api.get<{
    error?: string;
    code?: string;
    emailVerified?: boolean;
    email?: string;
    needsFirstUser?: boolean;
    needsLegalAcceptance?: boolean;
    subscriptionStatus?: string | null;
    user?: {
      id: string;
      name: string;
      role: string;
      initials: string;
      hasSeenTour?: boolean;
    };
    pharmacy?: {
      id: string;
      name: string;
      email?: string;
    };
  }>("/auth/current-user");
}

export function getMe() {
  return api.get<{
    id: string;
    name: string;
    email: string;
    emailVerified: boolean;
    subscriptionStatus: string;
    needsFirstUser: boolean;
    pharmacyName: string;
  }>("/users/me");
}

export function clearPassword() {
  return api.post("/auth/clear-password");
}

export function refreshSubscription() {
  return api.post("/auth/refresh-subscription");
}

// ── Authentication ──────────────────────────────────────────────────────

export function pharmacyLogin(data: { email: string; password: string }) {
  return api.post("/auth/pharmacy-login", data);
}

export function pharmacySignup(data: {
  password: string;
  npi: string;
  legalName: string;
  dbaName?: string;
  address?: {
    street: string;
    street2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  phone?: string;
  fax?: string;
  stateLicense?: string;
  licenseState?: string;
  adminEmail: string;
  timezone?: string;
  pharmacyType?: string;
  adminFirstName?: string;
  adminLastName?: string;
}) {
  return api.post("/auth/pharmacy-signup", data);
}

export function logout() {
  return api.post("/auth/logout");
}

export function refreshToken() {
  return api.post("/auth/refresh");
}

// ── Email Verification ──────────────────────────────────────────────────

export function verifyEmail(data: { token: string }) {
  return api.post("/auth/verify-email", data);
}

export function resendVerification(data?: { email: string }) {
  return api.post("/auth/resend-verification", data);
}

// ── Password Recovery ───────────────────────────────────────────────────

export function forgotPharmacyPassword(data: { email: string }) {
  return api.post("/auth/forgot-pharmacy-password", data);
}

export function verifyPharmacyReset(data: {
  otp: string;
  newPassword: string;
  confirmPassword: string;
}) {
  return api.post("/auth/verify-pharmacy-reset", data);
}

export function forceChangePassword(data: { newPassword: string }) {
  return api.post("/auth/force-change-password", data);
}

export function forgotStaffPasswordEligibility(initials: string) {
  return api.get("/auth/forgot-staff-password", { params: { initials } });
}

export function forgotStaffPassword(data: { initials: string }) {
  return api.post("/auth/forgot-staff-password", data);
}

export function verifyStaffReset(data: {
  otp: string;
  newPassword: string;
  confirmPassword: string;
}) {
  return api.post("/auth/verify-staff-reset", data);
}

// ── Staff Management ────────────────────────────────────────────────────

export function createFirstUser(data: {
  firstName: string;
  lastName: string;
  initials: string;
  password: string;
}) {
  return api.post("/auth/create-first-user", data);
}

export function getAuthUsers(params?: { pageSize?: number; page?: number }) {
  return api.get<{
    data?: {
      users: {
        id: string;
        initials: string;
        full_name: string | null;
        role: string;
      }[];
    };
    users?: {
      id: string;
      initials: string;
      full_name: string | null;
      role: string;
    }[];
  }>("/auth/users", { params });
}

export function validatePassword(data: { initials: string; password: string }) {
  return api.post("/auth/validate-password", data);
}

export function authorizeAction(data: {
  approverInitials: string;
  approverPassword: string;
  permission: string;
  context?: Record<string, never>;
}) {
  return api.post<{
    approvalToken: string;
    approver: { name: string; role: string; initials: string };
  }>("/auth/authorize-action", data);
}

// ── Two-Factor Authentication ──────────────────────────────────────────

export function setupPharmacy2fa() {
  return api.post<{ secret: string; qrUri: string }>(
    "/auth/2fa/pharmacy/setup"
  );
}

export function verifyPharmacy2faSetup(data: {
  code: string;
  type: "totp" | "recovery";
}) {
  return api.post<{ recoveryCodes: string[] }>(
    "/auth/2fa/pharmacy/verify",
    data
  );
}

export function disablePharmacy2fa(data: { password: string }) {
  return api.post("/auth/2fa/pharmacy/disable", data);
}

export function regeneratePharmacyRecoveryCodes(data: { password: string }) {
  return api.post<{ recoveryCodes: string[] }>(
    "/auth/2fa/pharmacy/recovery-codes",
    data
  );
}

export function setupStaff2fa() {
  return api.post<{ secret: string; qrUri: string }>("/auth/2fa/staff/setup");
}

export function verifyStaff2faSetup(data: {
  code: string;
  type: "totp" | "recovery";
}) {
  return api.post<{ recoveryCodes: string[] }>("/auth/2fa/staff/verify", data);
}

export function disableStaff2fa(data: { password: string }) {
  return api.post("/auth/2fa/staff/disable", data);
}

export function regenerateStaffRecoveryCodes(data: { password: string }) {
  return api.post<{ recoveryCodes: string[] }>(
    "/auth/2fa/staff/recovery-codes",
    data
  );
}

export function verifyPharmacy2faLogin(data: {
  code: string;
  type: "totp" | "recovery";
  rememberDevice?: boolean;
}) {
  return api.post("/auth/pharmacy-2fa-verify", data);
}

export function verifyStaff2faLogin(data: {
  code: string;
  type: "totp" | "recovery";
  rememberDevice?: boolean;
}) {
  return api.post("/auth/staff-2fa-verify", data);
}

export function setStaff2faEnforcement(data: { require2fa: boolean }) {
  return api.post("/auth/2fa/enforce", data);
}

export function get2faStatus() {
  return api.get<{
    pharmacyEnabled: boolean;
    staffEnabled: boolean;
    enforced: boolean;
  }>("/auth/2fa/status");
}

// ── NPI Lookup ──────────────────────────────────────────────────────────

export function npiLookup(npi: string) {
  return api.get("/auth/npi-lookup", { params: { npi } });
}
